import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAgendaAutoRefresh } from "@/hooks/useAgendaAutoRefresh";
import { financeAuthSessionId } from "@/lib/financeExecutor";

export const FINANCE_CHANGED = "vyv:finance-changed";
export const notifyFinanceChanged = (userId?: string) => window.dispatchEvent(new CustomEvent(FINANCE_CHANGED, { detail: { userId } }));
export type FinanceStatus = "loading" | "ready" | "error" | "signed-out";

/** Account-bound reads shared by the active Finance routes. */
export function useFinanceResource<T>(loader: (userId: string, assertCurrent: () => void) => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [status, setStatus] = useState<FinanceStatus>("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checkedAt, setCheckedAt] = useState<number | null>(null);
  const [identityGeneration, setIdentityGeneration] = useState(0);
  const owner = useRef<string | null | undefined>(undefined);
  const authSession = useRef<string | null>(null);
  const generation = useRef(0);
  const request = useRef(0);
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    const ticket = ++request.current;
    let epoch = generation.current;
    const current = () => mounted.current && ticket === request.current && epoch === generation.current;
    const assertCurrent = () => { if (!current()) throw new Error("Finance session changed"); };
    setIsRefreshing(true);
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      assertCurrent();
      if (error) throw error;
      const next = user?.id ?? null;
      if (owner.current !== next) {
        epoch = ++generation.current; setIdentityGeneration(epoch); owner.current = next;
        setUserId(next); setData(null); setCheckedAt(null);
      }
      if (!next) { setStatus("signed-out"); return; }
      const result = await loader(next, assertCurrent);
      assertCurrent(); setData(result); setStatus("ready"); setCheckedAt(Date.now());
    } catch (error) {
      if (current()) { console.error("[Finance] read failed", error); setStatus("error"); }
    } finally {
      if (current()) setIsRefreshing(false);
    }
  }, [loader]);

  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null;
      const nextSession = financeAuthSessionId(session?.access_token);
      if (!mounted.current || owner.current === next && authSession.current === nextSession) return;
      owner.current = next; authSession.current = nextSession; generation.current++; setIdentityGeneration(generation.current); request.current++;
      setUserId(next); setData(null); setCheckedAt(null); setIsRefreshing(false);
      setStatus(next ? "loading" : "signed-out");
      if (next) queueMicrotask(() => { if (mounted.current) void refresh(); });
    });
    void refresh();
    const changed = (event: Event) => { const changedOwner = (event as CustomEvent<{ userId?: string }>).detail?.userId; if (mounted.current && owner.current && (!changedOwner || changedOwner === owner.current)) void refresh(); };
    window.addEventListener(FINANCE_CHANGED, changed);
    return () => { mounted.current = false; generation.current++; request.current++; subscription.unsubscribe(); window.removeEventListener(FINANCE_CHANGED, changed); };
  }, [refresh]);

  useAgendaAutoRefresh({ checkedAt, nextEvent: null, loading: isRefreshing, error: status === "error", enabled: !!userId || status === "error", refresh });
  const commit = useCallback((update: (previous: T | null) => T) => {
    request.current++; setData(update); setCheckedAt(Date.now()); setIsRefreshing(false);
  }, []);
  const capture = useCallback(() => {
    const epoch = generation.current;
    const session = authSession.current;
    return () => mounted.current && owner.current === userId && !!userId && generation.current === epoch && authSession.current === session;
  }, [userId]);
  return { data, userId, status, isRefreshing, checkedAt, refresh, capture, commit, identityGeneration };
}
